import { ActionSheet } from '@src/components/ActionSheet';
import { AnimatedStackOutlet } from '@src/components/AnimatedStackOutlet';
import { GCashButton } from '@src/components/GCashButton';
import { LottieSpinner } from '@src/components/LottieSpinner';
import { OptimizedImage } from '@src/components/OptimizedImage';
import { useStackableRouteId } from '@src/hooks/useStackableRouteId';
import {
  EMPTY_ARRAY,
  useBalanceTransactionsContext,
  useObservableValue,
  usePortal,
  useSessionStorage,
  useWLOrdersProvider,
} from '@talos/kyoko';
import { useVirtualizer } from '@tanstack/react-virtual';
import clsx from 'clsx';
import { AnimatePresence, motion } from 'framer-motion';
import { take } from 'lodash-es';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useMatch } from 'react-router-dom';
import { combineLatest, map } from 'rxjs';
import { TransactionItem } from './TransactionItem';

export function TransactionsLayout() {
  const portalId = useStackableRouteId();
  const { setPortalRef } = usePortal(portalId);
  const match = useMatch('/transactions');

  return (
    <>
      <AnimatedStackOutlet />
      <div className={clsx('flex w-full grow', match === null && 'pointer-events-none')}>
        {/* Base background route: always rendered */}
        <TransactionsPage />
      </div>
      <div id={`${portalId}-portal`} ref={setPortalRef} />
    </>
  );
}

const HEADER_HEIGHT = 27;
const HEADER_HEIGHT_ITEM = 36;
const ITEM_HEIGHT = 85;

function TransactionsPage() {
  const { recentOrdersObs } = useWLOrdersProvider();
  const { transactionsListObs: balanceTransactionsListObs } = useBalanceTransactionsContext();

  // combine recentOrdersObs and recentTransactionsList and grab latest 10 items from them both
  const transactionsList = useObservableValue(
    () => {
      return combineLatest([recentOrdersObs, balanceTransactionsListObs]).pipe(
        map(([recentOrders, balanceTransactionsList]) => {
          const combinedList = [...take(recentOrders, 10), ...take(balanceTransactionsList, 10)];
          return combinedList.toSorted((a, b) => new Date(b.Timestamp!).getTime() - new Date(a.Timestamp!).getTime());
        })
      );
    },
    [recentOrdersObs, balanceTransactionsListObs],
    EMPTY_ARRAY
  );
  const showLoading = transactionsList === EMPTY_ARRAY;
  const showBlankScreen = transactionsList.length === 0;

  const [filter, setFilter] = useSessionStorage<{
    status: ('successful' | 'unsuccessful')[];
    transactionType: ('topup' | 'withdraw' | 'trade')[];
    dateRange: 'past7days' | 'past30days' | null;
  }>('transactions-filter-v2', {
    status: [],
    transactionType: [],
    dateRange: null,
  });

  const resetFilters = useCallback(() => {
    setFilter({
      status: [],
      transactionType: [],
      dateRange: null,
    });
  }, [setFilter]);

  const [appliedFilter, setAppliedFilter] = useState<typeof filter>(filter);
  const applyFilter = useCallback(() => {
    setAppliedFilter(filter);
  }, [filter]);

  const isEmptyFilter = useMemo(() => {
    return filter.status.length === 0 && filter.transactionType.length === 0 && filter.dateRange === null;
  }, [filter]);

  const filteredTransactionsList = useMemo(() => {
    const filter = appliedFilter;
    return transactionsList.filter(item => {
      // do statuses
      if (filter.status.length > 0) {
        if ('OrderID' in item) {
          if (item.OrdStatus === 'DoneForDay' || item.OrdStatus === 'Filled') {
            if (!filter.status.includes('successful')) {
              return false;
            }
          } else {
            if (!filter.status.includes('unsuccessful')) {
              return false;
            }
          }
        } else {
          if (item.Status === 'Completed') {
            if (!filter.status.includes('successful')) {
              return false;
            }
          } else {
            if (!filter.status.includes('unsuccessful')) {
              return false;
            }
          }
        }
      }
      // do transaction types
      if (filter.transactionType.length > 0) {
        if ('OrderID' in item) {
          if (!filter.transactionType.includes('trade')) {
            return false;
          }
        } else {
          if (item.TransactionType === 'Deposit') {
            if (!filter.transactionType.includes('topup')) {
              return false;
            }
          } else if (item.TransactionType === 'Withdrawal') {
            if (!filter.transactionType.includes('withdraw')) {
              return false;
            }
          }
        }
      }

      // do date range
      if (filter.dateRange) {
        if (filter.dateRange === 'past7days') {
          if (new Date(item.Timestamp!).getTime() < new Date().getTime() - 7 * 24 * 60 * 60 * 1000) {
            return false;
          }
        } else if (filter.dateRange === 'past30days') {
          if (new Date(item.Timestamp!).getTime() < new Date().getTime() - 30 * 24 * 60 * 60 * 1000) {
            return false;
          }
        }
      }

      return true;
    });
  }, [appliedFilter, transactionsList]);

  // Group transactions by date
  const groupedTransactions = useMemo(() => {
    const dateMap = new Map<string, typeof filteredTransactionsList>();

    filteredTransactionsList.forEach(item => {
      const date = new Date(item.Timestamp!).toDateString();
      if (!dateMap.has(date)) {
        dateMap.set(date, []);
      }
      dateMap.get(date)!.push(item);
    });

    // Convert to array and sort by date (newest first)
    return Array.from(dateMap.entries())
      .map(([date, items]) => ({ date, items }))
      .toSorted((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
  }, [filteredTransactionsList]);

  // Create virtualized items array with headers and items
  const virtualItems = useMemo(() => {
    const items: Array<{ type: 'header' | 'item'; date?: string; item?: any; index: number }> = [];
    let index = 0;

    groupedTransactions.forEach(group => {
      // Add date header
      items.push({
        type: 'header',
        date: group.date,
        index: index++,
      });

      // Add transaction items
      group.items.forEach(item => {
        items.push({
          type: 'item',
          item,
          index: index++,
        });
      });
    });

    return items;
  }, [groupedTransactions]);

  // Virtualization setup
  const parentRef = useRef<HTMLDivElement>(null);

  const rowVirtualizer = useVirtualizer({
    count: virtualItems.length,
    getScrollElement: () => parentRef.current,
    estimateSize: index => {
      const item = virtualItems[index];
      return item?.type === 'header' ? HEADER_HEIGHT_ITEM : ITEM_HEIGHT;
    },
    overscan: 5,
  });

  return (
    <ActionSheet
      trigger={({ open }) => (
        <div className="relative w-full" data-testid="transactions-page">
          <header className="bg-color-bg-primary sticky inset-x-0 top-0 z-10 grid h-[40px] grid-cols-[1fr_auto_1fr] place-items-center p-1 px-2">
            <span />
            <h1 className="text-color-text-inverse self-center text-base font-semibold tracking-wide">
              Transaction History
            </h1>
            <button
              onClick={open}
              className="text-color-text place-self-end px-3 py-1 text-sm font-medium"
              data-testid="filter-button"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                <g opacity="0.8">
                  <path
                    d="M9.00195 14.0996C10.2891 14.0998 11.3789 14.9388 11.7578 16.0996H20.002L20.0938 16.1045C20.5475 16.1507 20.9023 16.5341 20.9023 17C20.9021 17.4658 20.5474 17.8494 20.0938 17.8955L20.002 17.9004H11.7578C11.3787 19.0608 10.2888 19.9002 9.00195 19.9004C7.71492 19.9004 6.62423 19.0609 6.24512 17.9004H4.00195C3.50503 17.9004 3.10177 17.4969 3.10156 17C3.10156 16.5029 3.5049 16.0996 4.00195 16.0996H6.24512C6.62402 14.9387 7.71465 14.0996 9.00195 14.0996ZM9.00195 15.9004C8.39444 15.9004 7.90234 16.3925 7.90234 17C7.90255 17.6073 8.39457 18.0996 9.00195 18.0996C9.60916 18.0994 10.1014 17.6072 10.1016 17C10.1016 16.3926 9.60929 15.9006 9.00195 15.9004ZM15.002 9.09961C16.2891 9.09978 17.3789 9.93883 17.7578 11.0996H20.002C20.4988 11.0998 20.9023 11.5031 20.9023 12C20.9021 12.4967 20.4987 12.9002 20.002 12.9004H17.7578C17.3787 14.0608 16.2888 14.9002 15.002 14.9004C13.7149 14.9004 12.6242 14.0609 12.2451 12.9004H4.00195L3.91016 12.8955C3.45634 12.8495 3.10176 12.4659 3.10156 12C3.10156 11.5339 3.45621 11.1505 3.91016 11.1045L4.00195 11.0996H12.2451C12.624 9.93869 13.7147 9.09961 15.002 9.09961ZM15.002 10.9004C14.3944 10.9004 13.9023 11.3925 13.9023 12C13.9026 12.6073 14.3946 13.0996 15.002 13.0996C15.6092 13.0994 16.1014 12.6072 16.1016 12C16.1016 11.3926 15.6093 10.9006 15.002 10.9004ZM9.00195 4.09961C10.2891 4.09978 11.3789 4.93883 11.7578 6.09961H20.002L20.0938 6.10449C20.5475 6.15067 20.9023 6.53407 20.9023 7C20.9021 7.46578 20.5474 7.84938 20.0938 7.89551L20.002 7.90039H11.7578C11.3787 9.0608 10.2888 9.90022 9.00195 9.90039C7.71492 9.90039 6.62423 9.06095 6.24512 7.90039H4.00195C3.50503 7.90039 3.10177 7.49688 3.10156 7C3.10156 6.50294 3.5049 6.09961 4.00195 6.09961H6.24512C6.62402 4.93869 7.71465 4.09961 9.00195 4.09961ZM9.00195 5.90039C8.39444 5.90039 7.90234 6.39249 7.90234 7C7.90255 7.60733 8.39457 8.09961 9.00195 8.09961C9.60916 8.0994 10.1014 7.6072 10.1016 7C10.1016 6.39262 9.60929 5.9006 9.00195 5.90039Z"
                    fill="#ffffff"
                  />
                </g>
              </svg>
            </button>
          </header>
          <AnimatePresence>
            {showLoading ? (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
                className="m-auto flex size-full place-content-center place-items-center"
              >
                <div className="flex size-12">
                  <LottieSpinner />
                </div>
              </motion.div>
            ) : showBlankScreen ? (
              <>
                <div>
                  <div className="relative aspect-[720/1416] w-full">
                    <OptimizedImage name="beach" widths={[375, 750]} alt="beach" className="absolute inset-0 w-full" />
                  </div>
                  <div className="absolute inset-0 top-[45%] text-center">
                    <h2 className="text-xl font-bold">There&apos;s nothing here!</h2>
                    <h5 className="text-gray-600">Start trading and track all your transactions here.</h5>
                  </div>
                </div>
              </>
            ) : (
              <div
                className="w-full overflow-auto"
                style={{ height: `calc(100% - ${HEADER_HEIGHT}px)` }}
                ref={parentRef}
              >
                <div style={{ width: '100%', position: 'relative' }}>
                  {virtualItems.length === 0 ? (
                    <div className="p-4 text-center text-gray-500">No transactions found</div>
                  ) : (
                    rowVirtualizer.getVirtualItems().map((virtualRow, i) => {
                      const virtualItem = virtualItems[virtualRow.index];
                      // Apply styles depending on if this is the last item in its group
                      let isLastInGroup = false;
                      if (virtualItem.type === 'item') {
                        const nextVirtualItem = virtualItems[virtualRow.index + 1];
                        if (!nextVirtualItem || nextVirtualItem.type === 'header') {
                          isLastInGroup = true;
                        }
                      }

                      return (
                        <div
                          key={virtualRow.index}
                          style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: `${virtualRow.size}px`,
                            transform: `translateY(${virtualRow.start}px)`,
                          }}
                        >
                          {virtualItem.type === 'header' ? (
                            <div className="text-color-text px-4 py-2 text-base font-semibold tracking-wide">
                              {new Date(virtualItem.date!).toLocaleDateString('en-US', {
                                month: 'short',
                                day: 'numeric',
                                year: 'numeric',
                              })}
                            </div>
                          ) : (
                            <div
                              className={clsx('size-full', !isLastInGroup && 'border-color-border border-b')}
                              data-testid="transaction-item"
                            >
                              <TransactionItem
                                key={
                                  'OrderID' in virtualItem.item
                                    ? virtualItem.item.OrderID
                                    : virtualItem.item.TransactionID
                                }
                                entity={virtualItem.item}
                              />
                            </div>
                          )}
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            )}
          </AnimatePresence>
        </div>
      )}
      content={({ context, onClose }) => {
        return (
          <>
            {/* Header */}
            <div className="flex items-center justify-between px-4 pt-2">
              <h3 className="text-color-text text-2xl font-semibold">Filter</h3>
              <button onClick={onClose} data-testid="filter-close-button">
                <svg className="size-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Status Section */}
            <div className="px-4 py-2">
              <h4 className="text-color-text-weak mb-1 text-lg font-semibold">Status</h4>
              <div className="flex gap-2">
                <GCashButton
                  mode="toggle"
                  toggled={filter.status.includes('successful')}
                  onClick={() => {
                    setFilter(prev => ({
                      ...prev,
                      status: prev.status.includes('successful')
                        ? prev.status.filter(status => status !== 'successful')
                        : [...prev.status, 'successful'],
                    }));
                  }}
                  className="flex-none"
                  data-testid="filter-status-successful"
                >
                  Successful
                </GCashButton>
                <GCashButton
                  mode="toggle"
                  toggled={filter.status.includes('unsuccessful')}
                  onClick={() => {
                    setFilter(prev => ({
                      ...prev,
                      status: prev.status.includes('unsuccessful')
                        ? prev.status.filter(status => status !== 'unsuccessful')
                        : [...prev.status, 'unsuccessful'],
                    }));
                  }}
                  className="flex-none"
                  data-testid="filter-status-unsuccessful"
                >
                  Unsuccessful
                </GCashButton>
              </div>
            </div>

            {/* Transaction Type Section */}
            <div className="px-4 py-3">
              <h4 className="text-color-text-weak mb-1 text-lg font-semibold">Transaction Type</h4>
              <div className="flex flex-wrap gap-2">
                <GCashButton
                  mode="toggle"
                  toggled={filter.transactionType.includes('topup')}
                  onClick={() => {
                    setFilter(prev => ({
                      ...prev,
                      transactionType: prev.transactionType.includes('topup')
                        ? prev.transactionType.filter(type => type !== 'topup')
                        : [...prev.transactionType, 'topup'],
                    }));
                  }}
                  className="flex-none"
                  data-testid="filter-transaction-type-topup"
                >
                  Top Up
                </GCashButton>
                <GCashButton
                  mode="toggle"
                  toggled={filter.transactionType.includes('withdraw')}
                  onClick={() => {
                    setFilter(prev => ({
                      ...prev,
                      transactionType: prev.transactionType.includes('withdraw')
                        ? prev.transactionType.filter(type => type !== 'withdraw')
                        : [...prev.transactionType, 'withdraw'],
                    }));
                  }}
                  className="flex-none"
                  data-testid="filter-transaction-type-withdraw"
                >
                  Withdraw
                </GCashButton>
                <GCashButton
                  mode="toggle"
                  toggled={filter.transactionType.includes('trade')}
                  onClick={() => {
                    setFilter(prev => ({
                      ...prev,
                      transactionType: prev.transactionType.includes('trade')
                        ? prev.transactionType.filter(type => type !== 'trade')
                        : [...prev.transactionType, 'trade'],
                    }));
                  }}
                  className="flex-none"
                  data-testid="filter-transaction-type-trade"
                >
                  Trade
                </GCashButton>
              </div>
            </div>

            {/* Date Range Section */}
            <div className="px-4 py-2">
              <h4 className="text-color-text-weak mb-1 text-lg font-semibold">Date Range</h4>
              <div className="flex gap-2">
                <GCashButton
                  mode="toggle"
                  toggled={filter.dateRange === 'past7days'}
                  onClick={() => {
                    setFilter(prev => ({ ...prev, dateRange: 'past7days' }));
                  }}
                  className="flex-none"
                  data-testid="filter-date-range-past7days"
                >
                  Past 7 days
                </GCashButton>
                <GCashButton
                  mode="toggle"
                  toggled={filter.dateRange === 'past30days'}
                  onClick={() => {
                    setFilter(prev => ({ ...prev, dateRange: 'past30days' }));
                  }}
                  className="flex-none"
                  data-testid="filter-date-range-past30days"
                >
                  Past 30 days
                </GCashButton>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3 px-4 py-2">
              <GCashButton
                size="lg"
                variant="secondary"
                onClick={resetFilters}
                disabled={isEmptyFilter}
                data-testid="filter-reset-button"
              >
                Reset Filters
              </GCashButton>
              <GCashButton
                size="lg"
                variant="primary"
                onClick={() => {
                  applyFilter();
                  onClose();
                }}
                data-testid="filter-apply-button"
              >
                Apply
              </GCashButton>
            </div>
          </>
        );
      }}
    />
  );
}
